import React, { useEffect, useMemo, useState ,useRef} from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  Box,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import EventRoundedIcon from "@mui/icons-material/EventRounded";

import {allowedUsers } from "./constants"
import {getStampColor,LEAVE_COLOR } from "./constants"

import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";

dayjs.extend(customParseFormat);

// --------------------
// helpers
// --------------------
function normalizeTimeFlexible(input) {
  const raw = String(input ?? "").trim();
  if (!raw) return { ok: true, hhmm: "" };

  if (/^\d{1,2}$/.test(raw)) {
    const h = Number(raw);
    if (h >= 0 && h <= 23) return { ok: true, hhmm: String(h).padStart(2, "0") + ":00" };
    return { ok: false, hhmm: "" };
  }

  if (/^\d{3,4}$/.test(raw)) {
    const s = raw.padStart(4, "0");
    const hh = Number(s.slice(0, 2));
    const mm = Number(s.slice(2, 4));
    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
      return { ok: true, hhmm: String(hh).padStart(2, "0") + ":" + String(mm).padStart(2, "0") };
    }
    return { ok: false, hhmm: "" };
  }

  const m = raw.match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);
  if (m) {
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
      return { ok: true, hhmm: String(hh).padStart(2, "0") + ":" + String(mm).padStart(2, "0") };
    }
    return { ok: false, hhmm: "" };
  }

  const parsed = dayjs(raw, ["H:mm", "HH:mm", "H", "HH"], true);
  if (parsed.isValid()) return { ok: true, hhmm: parsed.format("HH:mm") };

  return { ok: false, hhmm: "" };
}

function hhmmToDayjs(hhmm) {
  const t = String(hhmm ?? "").trim();
  if (!t) return null;
  const parsed = dayjs(t, ["HH:mm", "H:mm"], true);
  return parsed.isValid() ? parsed : null;
}

function makePairFromTimes(inStr = "", outStr = "") {
  const n1 = normalizeTimeFlexible(inStr);
  const n2 = normalizeTimeFlexible(outStr);
  return {
    isLeave: false,
    leaveid: "DAWAM",
    inText: n1.ok ? n1.hhmm : String(inStr ?? "").trim(),
    outText: n2.ok ? n2.hhmm : String(outStr ?? "").trim(),
    inPicker: hhmmToDayjs(n1.ok ? n1.hhmm : ""),
    outPicker: hhmmToDayjs(n2.ok ? n2.hhmm : ""),
  };
}

const normalizeApiBase = (raw) => {
  const s = String(raw ?? "").trim().replace(/\/$/, "");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return s;
};

const pairsFromAttendance = (att) => {
  if (!att) return null;

  if (Array.isArray(att.segments) && att.segments.length) {
    const segs = att.segments.map((s) => {
      const p = makePairFromTimes(s.time_in ?? "", s.time_out ?? "");
      const mode = String(s.mode ?? "").toUpperCase();
      if (mode === "LEAVE") {
        p.isLeave = true;
        p.leaveid = String(s.leaveid ?? "");
      } else {
        p.isLeave = false;
        p.leaveid = "DAWAM";
      }
      return p;
    });
    return segs.length ? segs : null;
  }

  const in1 = att.timeIn ?? att.in ?? "";
  const out1 = att.timeOut ?? att.out ?? "";
  if (in1 || out1) return [makePairFromTimes(in1, out1)];

  return null;
};

function summarizePairsLines(pairs, leaveOptions) {
  const nameById = new Map((leaveOptions || []).map((x) => [String(x.leaveid), String(x.leavename || "")]));

  const fmtRange = (p) => {
    const a = String(p?.inText ?? "").trim();
    const b = String(p?.outText ?? "").trim();
    if (a && b) return `${a} ~ ${b}`;
    if (a) return `${a}`;
    if (b) return `${b}`;
    return "";
  };

const grouped = new Map();

  (pairs || []).forEach((p) => {
    if (!p) return;
    const range = fmtRange(p);
    if (!range) return;

    const isLeave = !!p.isLeave;
    const leaveid = String(p.leaveid || "");

    const label = isLeave ? nameById.get(leaveid) || (leaveid ? `Leave ${leaveid}` : "Leave") : "working";

    const startMin = (() => {
      const s = String(p.inText ?? "").trim();
      if (!s) return Infinity;
      const [h, m] = s.split(":").map((n) => parseInt(n, 10));
      return Number.isNaN(h) ? Infinity : h * 60 + (Number.isNaN(m) ? 0 : m);
    })();

    if (!grouped.has(label)) grouped.set(label, { variant: isLeave ? "leave" : "work", ranges: [], minStart: Infinity });
    const g = grouped.get(label);
    g.ranges.push(range);
    g.minStart = Math.min(g.minStart, startMin);
  });

  if (!grouped.size) return [];

  const lines = Array.from(grouped.entries()).map(([label, obj]) => {
    const uniqueRanges = Array.from(new Set(obj.ranges));
    return { key: `${obj.variant}_${label}`, variant: obj.variant, label, value: uniqueRanges.join(" | "), minStart: obj.minStart };
  });

  lines.sort((a, b) => a.minStart - b.minStart);

  return lines;
}

// ✅ ignore noisy backend message
function shouldIgnoreApiMessage(msg) {
  const s = String(msg || "").trim();
  return /missing\s*\?from\s*=\s*yyyy-mm-dd\s*&\s*to\s*=\s*yyyy-mm-dd/i.test(s);
}

// --------------------
// component
// --------------------
const EditTimeDialog = React.memo(function EditTimeDialog({
  open,
  onClose,
  onSave, // parent local update
  // title,
  empName,
  address,
  initialTimes,
  setError,
  empId,
  isoDate,
  cellSegments, // [{mode,leaveid,time_in,time_out}, ...]
  stampModules,
}) {
  const [vacationTypes, setVacationTypes] = useState([]);
  const [vacLoading, setVacLoading] = useState(false);
  const [vacError, setVacError] = useState("");

  const typeOptions = useMemo(() => {
    const base = [{ leaveid: "DAWAM", leavename: "working" }];
    const api = Array.isArray(vacationTypes) ? vacationTypes : [];
    const map = new Map();
    [...base, ...api].forEach((x) =>
      map.set(String(x.leaveid), { leaveid: String(x.leaveid), leavename: String(x.leavename || "") })
    );
    return Array.from(map.values());
  }, [vacationTypes]);

  const [pairs, setPairs] = useState([makePairFromTimes("", "")]);

  //to jump from in to out
  
  const dialogContentRef = useRef(null); 
  const inPickerRefs = useRef([]);
  const outPickerRefs = useRef([]);


  const [errors, setErrors] = useState({});
  const [attLoading, setAttLoading] = useState(false);
  const [attError, setAttError] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // switch: when ON, Save applies these segments to ALL employees instead of just this one
  const [saveForAll, setSaveForAll] = useState(false);

  const summaryLines = useMemo(() => summarizePairsLines(pairs, typeOptions), [pairs, typeOptions]);

  useEffect(() => {
    
    if (!open) return;
        //console.log("EED", { initialRawTime, initialRawStatus, initialStampModules });

    // init pairs
        if (Array.isArray(cellSegments) && cellSegments.length) {
          console.log("ETD", { stampModules, cellSegments });
          const modules = String(stampModules || "")
            .split("|")
            .map((v) => parseInt(v.trim(), 10));
          let workIdx = 0;
          const segs = cellSegments.map((s) => {
            const p = makePairFromTimes(s.time_in ?? "", s.time_out ?? "");
            const mode = String(s.mode ?? "").toUpperCase();
            if (mode === "LEAVE") {
              p.isLeave = true;
              p.leaveid = String(s.leaveid ?? "");
            } else {
              p.isLeave = false;
              p.leaveid = "DAWAM";
            }
            p.isExisting = true;
            p.inModule = !p.isLeave && p.inText ? modules[workIdx++] : null;
            p.outModule = !p.isLeave && p.outText ? modules[workIdx++] : null;
            return p;
          });
      setPairs(segs.length ? segs : [makePairFromTimes("", "")]);
    } else {
      const init = Array.isArray(initialTimes) ? initialTimes : [];
      const built = [];
      for (let i = 0; i < init.length; i += 2) built.push(makePairFromTimes(init[i] ?? "", init[i + 1] ?? ""));
      setPairs(built.length ? built : [makePairFromTimes("", "")]);
    }

    setErrors({});
    setAttError("");
    setSaveError("");
    setVacError("");
    setSaveForAll(false);

    const apiUrlRaw = localStorage.getItem("saved_sett_api_url");
    const apiUrl = normalizeApiBase(apiUrlRaw);

    if (!apiUrl) {
      setVacationTypes([]);
      setVacError("Missing API URL in localStorage (saved_sett_api_url).");
      return;
    }

    // load leave types
    setVacLoading(true);
    axios
      .get(`${apiUrl}/api/vacation-types`, { withCredentials: true })
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data.map((r) => ({
              leaveid: r.LeaveId ?? r.leaveid ?? r.Code,
              leavename: r.LeaveName ?? r.leavename ?? r.ArbStringValue ?? "",
            }))
          : [];
        setVacationTypes(list);
      })
      .catch((error) => {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load vacation types.";

        // ✅ silently ignore
        if (shouldIgnoreApiMessage(msg)) return;

        setVacationTypes([]);
        setVacError(msg);
        if (setError) setError(msg);
      })
      .finally(() => setVacLoading(false));

    // optional fetch authoritative
    if (empId == null || !isoDate) return;

    setAttLoading(true);
    axios
      .get(`${apiUrl}/api/attendances`, { params: { empId, date: isoDate }, withCredentials: true })
      .then((res) => {
        const data = res.data;
        const att = Array.isArray(data)
          ? data.find((x) => String(x.empId ?? x.emp_id ?? "") === String(empId)) || data[0]
          : data;

        const loadedPairs = pairsFromAttendance(att);
        if (loadedPairs && loadedPairs.length) setPairs(loadedPairs);
      })
      .catch((error) => {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load attendance.";

        // ✅ silently ignore
        if (shouldIgnoreApiMessage(msg)) return;

        setAttError(msg);
      })
      .finally(() => setAttLoading(false));
  }, [open, initialTimes, setError, empId, isoDate, cellSegments, stampModules]);

  const addPair = () => setPairs((p) => [...p, makePairFromTimes("", "")]);

  const removePair = (idx) => {
    setPairs((p) => {
      if (p.length <= 1) return p;
      const next = p.slice();
      next.splice(idx, 1);
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`p${idx}_`)) delete next[k];
      });
      return next;
    });
  };

 

  const setPair = (idx, patch) => {
    setPairs((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

   const getTimeTextColor = (p, which) => {
    if (!p.isExisting) return undefined;
    if (p.isLeave) return LEAVE_COLOR;
    const mod = which === "in" ? p.inModule : p.outModule;
    if (mod === null || mod === undefined) return undefined;
    return getStampColor(mod);
  };

  const timeFieldSx = (p, which) => {
    const color = getTimeTextColor(p, which);
    return {
      backgroundColor: "white",
      borderRadius: 2,
      "& .MuiOutlinedInput-root": { borderRadius: 2 },
      ...(color && {
        "& .MuiInputBase-root, & .MuiPickersInputBase-root": {
          color,
          fontWeight: 800,
        },
        "& .MuiInputBase-input, & .MuiPickersSectionList-root": {
          color,
          WebkitTextFillColor: color,
          fontWeight: 800,
        },
      }),
    };
  }; 


  // !!!!!
  const validate = () => {
    const errs = {};
    const normalizedPairs = pairs.map((p, i) => {
      if (p.isLeave) {
        if (!p.leaveid || String(p.leaveid) === "DAWAM")
          errs[`p${i}_leave`] = "Select leave type";
      }

      // If both Time In and Time Out are empty, treat this pair as
      // intentionally blank — not required, not an error. This lets HR
      // clear a mistaken entry (via the delete button) and save, which
      // correctly results in nothing being sent for this pair, so the
      // backend's existing delete-then-insert logic just removes the
      // old record with nothing replacing it.
      const inEmpty = !p.inText || !String(p.inText).trim();
      const outEmpty = !p.outText || !String(p.outText).trim();

      if (inEmpty && outEmpty) {
        return {
          ...p,
          inText: "",
          outText: "",
          inPicker: null,
          outPicker: null,
        };
      }

      const n1 = normalizeTimeFlexible(p.inText);
      const n2 = normalizeTimeFlexible(p.outText);

      // if (!n1.ok) errs[`p${i}_in`] = "Invalid time";
      // if (!n2.ok) errs[`p${i}_out`] = "Invalid time"; // ← blocks empty outText
     
      // time_in is required 20/5/26
      if (p.inText && !n1.ok) errs[`p${i}_in`] = "Invalid time";

      // time_out is OPTIONAL — only validate if provided
      if (p.outText && !n2.ok) errs[`p${i}_out`] = "Invalid time";

      //must have at least time_in 26/5
      //if (!p.inText && !p.outText) errs[`p${i}_in`] = "At least Time In is required";

      const inHH = n1.ok ? n1.hhmm : p.inText;
      const outHH = n2.ok ? n2.hhmm : p.outText;

      if (n1.ok && n2.ok && inHH && outHH) {
        const a = dayjs(inHH, ["HH:mm"], true);
        const b = dayjs(outHH, ["HH:mm"], true);
        if (a.isValid() && b.isValid() && b.isBefore(a))
          errs[`p${i}_out`] = "Time Out must be after Time In";
      }

      return {
        ...p,
        inText: n1.ok ? n1.hhmm : p.inText,
        outText: n2.ok ? n2.hhmm : p.outText,
        inPicker: hhmmToDayjs(n1.ok ? n1.hhmm : ""),
        outPicker: hhmmToDayjs(n2.ok ? n2.hhmm : ""),
      };
    });

    // Only run overlap checks if no basic errors exist
    if (Object.keys(errs).length === 0) {
      const toMin = (hhmm) => {
        const parts = String(hhmm).split(":");
        return parseInt(parts[0], 10) * 60 + parseInt(parts[1] || "0", 10);
      };

      // Build segments with their original index in normalizedPairs
      const segments = normalizedPairs
        .map((p, i) => ({
          idx: i,
          isLeave: p.isLeave,
          start: p.inText ? toMin(p.inText) : null,
          end: p.outText ? toMin(p.outText) : null,
        }))
        .filter((s) => s.start !== null && s.end !== null && s.end > s.start);

      const workSegs = segments.filter((s) => !s.isLeave);
      const leaveSegs = segments.filter((s) => s.isLeave);

      // Rule 1: leave must NOT overlap or be inside a work segment
      // HR must split: Work 08:00~09:00 + Leave 09:00~10:00 + Work 10:00~16:00
      for (const leave of leaveSegs) {
        const overlapsWork = workSegs.some(
          (work) => leave.start < work.end && work.start < leave.end
        );
        if (overlapsWork) {
          errs[`p${leave.idx}_in`] =
            `❌ Leave ${normalizedPairs[leave.idx].inText}~${normalizedPairs[leave.idx].outText} overlaps a work segment. You must split: e.g. Work 08:00~09:00 + Leave 09:00~10:00 + Work 10:00~16:00`;
        }
      }

      //! FOR WORK VS WORK SORT DO NOT SEND AN ERROR OF OVERLAP SO WE COMMENT RULE 2 AND ADD NEW CODE  
      // Rule 2: two work segments must not overlap
      // for (let i = 0; i < workSegs.length; i++) {
      //   for (let j = i + 1; j < workSegs.length; j++) {
      //     const a = workSegs[i];
      //     const b = workSegs[j];
      //     if (a.start < b.end && b.start < a.end) {
      //       errs[`p${b.idx}_in`] =
      //         `Work segments overlap: ${normalizedPairs[a.idx].inText}~${normalizedPairs[a.idx].outText} and ${normalizedPairs[b.idx].inText}~${normalizedPairs[b.idx].outText}`;
      //     }
      //   }
      // }

      // Work vs Work → AUTO SORT (no error)
      const hasWorkWorkOverlap = workSegs.some((a, i) =>
          workSegs.some((b, j) => i !== j && a.start < b.end && b.start < a.end)
      );

      if (hasWorkWorkOverlap) {
          // collect ALL work times and sort
          const allTimes = [];
          workSegs.forEach(s => {
              if (s.start !== null) allTimes.push(s.start);
              if (s.end !== null)   allTimes.push(s.end);
          });
          allTimes.sort((a, b) => a - b);

          // rebuild pairs
          const toHHMM = (m) => 
              `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
          
          const newWorkPairs = [];
          for (let i = 0; i + 1 < allTimes.length; i += 2) {
              newWorkPairs.push(makePairFromTimes(
                  toHHMM(allTimes[i]), 
                  toHHMM(allTimes[i+1])
              ));
          }
          // replace work pairs with sorted ones
          // keep leave pairs unchanged
          const leavePairs = pairs.filter(p => p.isLeave);
          setPairs([...newWorkPairs, ...leavePairs]);
          // continue save ✅ no error
      }

      // Rule 3: two leave segments must not overlap
      for (let i = 0; i < leaveSegs.length; i++) {
        for (let j = i + 1; j < leaveSegs.length; j++) {
          const a = leaveSegs[i];
          const b = leaveSegs[j];
          if (a.start < b.end && b.start < a.end) {
            errs[`p${b.idx}_in`] =
              `Leave segments overlap: ${normalizedPairs[a.idx].inText}~${normalizedPairs[a.idx].outText} and ${normalizedPairs[b.idx].inText}~${normalizedPairs[b.idx].outText}`;
          }
        }
      }
    }

    setErrors(errs);
    if (Object.keys(errs).length) return { ok: false, normalizedPairs };
    setPairs(normalizedPairs);
    return { ok: true, normalizedPairs };
  };

  //* Delete button 
  const clearPair = (idx) => {
  setPairs(prev =>
    prev.map((p, i) => i === idx ? { ...p, inText: "", outText: "", inPicker: null, outPicker: null, isLeave: false, leaveid: "DAWAM" } : p)
  );
};

  
  const handleSave = async () => {
    const { ok, normalizedPairs } = validate();
    if (!ok) return;

    // Skip any pair where both times are blank — HR clearing a mistaken
    // entry via delete shouldn't send an empty segment, it should just
    // send nothing for that pair, so the backend's delete-then-insert
    // logic correctly removes the old record with nothing replacing it.
    const segments = normalizedPairs
      .filter((p) => String(p.inText ?? "").trim() || String(p.outText ?? "").trim())
      .map((p) => ({
        mode: p.isLeave ? "LEAVE" : "WORK",
        leaveid: p.isLeave ? String(p.leaveid ?? "") : "DAWAM",
        leave_type: p.isLeave ? String(p.leaveid ?? "") : 0,
        time_in: String(p.inText ?? ""),
        time_out: String(p.outText ?? ""),
      }));

    // const segments = normalizedPairs.map((p) => ({
    //   mode: p.isLeave ? "LEAVE" : "WORK",
    //   leaveid: p.isLeave ? String(p.leaveid ?? "") : "DAWAM",
    //   leave_type: p.isLeave ? String(p.leaveid ?? "") : 0,
    //   time_in: String(p.inText ?? ""),
    //   time_out: String(p.outText ?? ""),
    // }));

    const isHr = allowedUsers.includes(localStorage.getItem("saved_username") || "");

    const apiUrlRaw = localStorage.getItem("saved_sett_api_url");
    const apiUrl = normalizeApiBase(apiUrlRaw);

    if (!apiUrl) {
      setSaveError("Missing API URL in localStorage (saved_sett_api_url).");
      return;
    }
    if (!isoDate) {
      setSaveError("Missing date.");
      return;
    }
    if (!saveForAll && empId == null) {
      setSaveError("Missing empId.");
      return;
    }

    setSaving(true);
    setSaveError("");

    try {
      const callerUid = localStorage.getItem("saved_username") || "";

      if (saveForAll) {
        // Switch is ON — apply these exact segments to every active employee for this date
        await axios.post(
          `${apiUrl}/api/schedule/bulk`,
          { date: isoDate, segments, comment: "", callerUid, isHr },
          { withCredentials: true }
        );
      } else {
        // Normal single-employee save
        await axios.post(
          `${apiUrl}/api/schedule`,
          { empId, date: isoDate, segments, comment: "",callerUid , isHr },
          { withCredentials: true }
        );
      }

      if (onSave) onSave(segments);
      if (onClose) onClose();
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        (saveForAll ? "Failed to save schedule for all employees." : "Failed to save schedule.");

      // ✅ silently ignore
      if (shouldIgnoreApiMessage(msg)) return;

      setSaveError(msg);
      if (setError) setError(msg);
    } finally {
      setSaving(false);
    }
  };

  

  const focusField = (idx, field) => {
    const root = dialogContentRef.current;
    if (!root) return;
    const el = root.querySelector(`input[data-pair-idx="${idx}"][data-field="${field}"]`);
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, color: "#4a148c" }}>{empName}</DialogTitle>
     {/* To put the date i double click  inside this dialog !! */}
        <Box sx={{ px: 3, pt: 0, pb: 1, mt: -1 }}>
           <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              icon={<EventRoundedIcon sx={{ fontSize: 18 }} />}
              label={isoDate ? dayjs(isoDate).format("dddd, D MMM YYYY") : ""}
              size="small"
              sx={{
                fontWeight: 800,
                bgcolor: "rgba(0,121,107,.08)",
                color: "#004d40",
              }}
            />
            {/* {empName && (
              <Chip
                label={empName}
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: "rgba(74,20,140,.08)",
                  color: "#4a148c",
                }}
              />
            )} */}
          </Stack>
      </Box> 

      <DialogContent dividers ref={dialogContentRef} sx={{ background: "linear-gradient(180deg, #fbfffe 0%, #ffffff 100%)" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={1.4}>
            {address ? (
              <Paper
                elevation={0}
                sx={{
                  p: 1.2,
                  borderRadius: 2,
                  border: "1px solid rgba(178,223,219,.9)",
                  bgcolor: "rgba(224,242,241,.45)",
                  color: "#004d40",
                  fontWeight: 800,
                }}
              >
                {address}
              </Paper>
            ) : null}

            <Paper
              elevation={0}
              sx={{
                p: 1.2,
                borderRadius: 2,
                border: "1px solid rgba(178,223,219,.9)",
                bgcolor: "rgba(224,242,241,.18)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {summaryLines.length ? (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 1,
                        borderRadius: 2,
                        border: "1px solid rgba(0,0,0,.06)",
                        bgcolor: "rgba(0,0,0,.02)",
                      }}
                    >
                      <Stack spacing={0.5}>
                        {summaryLines.map((l) => (
                          <Box key={l.key} sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                            <Typography
                              sx={{
                                minWidth: 90,
                                fontWeight: 900,
                                color: l.variant === "work" ? "#004d40" : "#4a148c",
                                textTransform: "none",
                              }}
                            >
                              {String(l.label)}:
                            </Typography>
                            <Typography sx={{ fontWeight: 800, opacity: 0.85 }}>{l.value}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {attLoading ? <CircularProgress size={18} /> : null}

                  <IconButton
                    onClick={addPair}
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      background: "linear-gradient(90deg,#00796b,#26a69a)",
                      color: "white",
                      boxShadow: "0 10px 24px rgba(0,0,0,.18)",
                      "&:hover": { opacity: 0.92, background: "linear-gradient(90deg,#00796b,#26a69a)" },
                    }}
                  >
                    <AddCircleRoundedIcon />
                  </IconButton>
                </Stack>
              </Box>

              {attError ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {attError}
                </Alert>
              ) : null}
              {vacError ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {vacError}
                </Alert>
              ) : null}
              {saveError ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {saveError}
                </Alert>
              ) : null}
              {/* !!!To specify the error (overlap) */}
              {Object.keys(errors).length > 0 ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <strong>⚠ Cannot save — please fix:</strong>
                  <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                    {Object.values(errors)
                      .filter((msg, i, arr) => arr.indexOf(msg) === i) 
                      .map((msg, i) => (
                        <li key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                          {msg}
                        </li>
                      ))}
                  </ul>
                </Alert>
              ) : null}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                border: saveForAll ? "1px solid #ef9a9a" : "1px solid rgba(0,0,0,.06)",
                bgcolor: saveForAll ? "rgba(255,235,238,.6)" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 13, color: saveForAll ? "#b71c1c" : "text.secondary" }}>
                {saveForAll
                  ? "⚠ Save will apply to ALL employees on this date"
                  : "Save for all employees"}
              </Typography>
              <Switch
                checked={saveForAll}
                disabled={saving}
                onChange={(e) => setSaveForAll(e.target.checked)}
                color="error"
              />
            </Paper>

            <Divider sx={{ opacity: 0.5 }} />

            {pairs.map((p, idx) => {
              const inErr = errors[`p${idx}_in`];
              const outErr = errors[`p${idx}_out`];
              const leaveErr = errors[`p${idx}_leave`];

              return (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,.08)",
                    background: p.isLeave
                      ? "linear-gradient(180deg, rgba(255,248,225,.75) 0%, #fff 70%)"
                      : "linear-gradient(180deg, rgba(224,242,241,.35) 0%, #fff 70%)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        icon={p.isLeave ? <EventBusyRoundedIcon /> : <WorkRoundedIcon />}
                        label={p.isLeave ? "Leave" : "Work"}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          bgcolor: p.isLeave ? "rgba(255,193,7,.15)" : "rgba(38,166,154,.12)",
                        }}
                      />
                      <Typography sx={{ fontWeight: 900, opacity: 0.75 }}>#{idx + 1}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Switch
                            checked={p.isLeave}
                            disabled={saving}
                            onChange={(e) => {
                              const isLeave = e.target.checked;
                              setPair(idx, {
                                isLeave,
                                leaveid: isLeave ? (String(p.leaveid || "") === "DAWAM" ? "" : p.leaveid) : "DAWAM",
                                isExisting: false,
                              });
                              setErrors((prev) => {
                                const next = { ...prev };
                                delete next[`p${idx}_leave`];
                                return next;
                              });
                            }}
                          />
                        }
                        label={<Typography sx={{ fontWeight: 800 }}>Leave</Typography>}
                      />
                      
                      <Tooltip title="Remove">
                        <span>
                          <IconButton
                            disabled={saving}
                            onClick={() => {
                              if (pairs.length <= 1) {
                                clearPair(idx);
                              } else {
                                removePair(idx);
                              }
                            }}
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              border: "1px solid rgba(0,0,0,.08)",
                            }}
                          >
                            <DeleteRoundedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box> 
                    </Box> 

                  <Box sx={{ mt: 1.1, display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 1 }}>
                   <TimePicker
                      label="Time In"
                      value={p.inPicker}
                      ref={(el) => { inPickerRefs.current[idx] = el; }}
                      onChange={(v) => {
                        const d = v ? dayjs(v) : null;
                        const hhmm = d && d.isValid() ? d.format("HH:mm") : "";
                        setPair(idx, { inPicker: d && d.isValid() ? d : null, inText: hhmm, inModule: null });
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next[`p${idx}_in`];
                          return next;
                        });
                      }}
                      ampm={false}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!inErr,
                          helperText: inErr ? inErr : " ",
                          disabled: saving,
                          sx: timeFieldSx(p, "in"), 
                          InputProps: {
                            startAdornment: (
                              <AccessTimeRoundedIcon fontSize="small" style={{ opacity: 0.7, marginRight: 8 }} />
                            ),
                          },
                          inputProps: {
                            "data-pair-idx": idx,
                            "data-field": "in",
                          },
                          onClick: () => {
                            inPickerRefs.current[idx]?.setSelectedSections?.("hours");
                          },
                          onKeyDown: (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              focusField(idx, "out");
                            }
                          },
                        },
                      }}
                    />
                  {/* Swap time in time out  */}
                    <Tooltip title="Swap Time In / Time Out">
                      <span style={{ alignSelf: "start", marginTop: 6 }}>
                        <IconButton
                          size="small"
                          disabled={saving || (!p.inText && !p.outText)}
                          onClick={() => {
                            setPair(idx, {
                              inText: p.outText,
                              outText: p.inText,
                              inPicker: p.outPicker,
                              outPicker: p.inPicker,
                              inModule: p.outModule,
                              outModule: p.inModule,
                            });
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next[`p${idx}_in`];
                              delete next[`p${idx}_out`];
                              return next;
                            });
                          }}
                          sx={{ color: "#00796b" }}
                        >
                          <SwapHorizRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <TimePicker
                      label="Time Out"
                      value={p.outPicker}
                      ref={(el) => { outPickerRefs.current[idx] = el; }}
                      onChange={(v) => {
                        const d = v ? dayjs(v) : null;
                        const hhmm = d && d.isValid() ? d.format("HH:mm") : "";
                        setPair(idx, { outPicker: d && d.isValid() ? d : null, outText: hhmm, outModule: null });
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next[`p${idx}_out`];
                          return next;
                        });
                      }}
                      ampm={false}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!outErr,
                          helperText: outErr ? outErr : " ",
                          disabled: saving,
                          sx: timeFieldSx(p, "out"),
                          InputProps: {
                            startAdornment: (
                              <AccessTimeRoundedIcon fontSize="small" style={{ opacity: 0.7, marginRight: 8 }} />
                            ),
                          },
                          inputProps: {
                            "data-pair-idx": idx,
                            "data-field": "out",
                          },
                          onClick: () => {
                            outPickerRefs.current[idx]?.setSelectedSections?.("hours");
                          },
                          onKeyDown: (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              const hasNext = idx + 1 < pairs.length;
                              if (hasNext) {
                                focusField(idx + 1, "in");
                              } else {
                                document.activeElement?.blur?.();
                              }
                            }
                          },
                        },
                      }}
                    />

                  </Box>

                  <Box sx={{ mt: 2 }} />

                  <FormControl
                    fullWidth
                    size="small"
                    disabled={!p.isLeave || saving}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" } }}
                  >
                    <InputLabel id={`leave-type-${idx}`}>Leave Type</InputLabel>
                    <Select
                      labelId={`leave-type-${idx}`}
                      label="Leave Type"
                      value={p.isLeave ? String(p.leaveid || "") : "DAWAM"}
                      onChange={(e) => {
                        setPair(idx, { leaveid: String(e.target.value) });
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next[`p${idx}_leave`];
                          return next;
                        });
                      }}
                      MenuProps={{ PaperProps: { sx: { borderRadius: 2 } } }}
                      endAdornment={vacLoading ? <CircularProgress size={18} sx={{ mr: 1.5 }} /> : null}
                    >
                      {p.isLeave ? (
                        <MenuItem value="">
                          <Typography sx={{ opacity: 0.7, fontStyle: "italic" }}>Select…</Typography>
                        </MenuItem>
                      ) : null}

                      {typeOptions
                        .filter((x) => String(x.leaveid) !== "DAWAM")
                        .map((opt) => (
                          <MenuItem key={String(opt.leaveid)} value={String(opt.leaveid)}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 1 }}>
                              <Typography sx={{ fontWeight: 800 }}>{opt.leavename}</Typography>
                              <Typography sx={{ opacity: 0.55, fontSize: 12 }}>{String(opt.leaveid)}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  {leaveErr ? (
                    <Typography sx={{ mt: 0.4, fontSize: 12, color: "error.main" }}>{leaveErr}</Typography>
                  ) : null}
                </Paper>
              );
            })}
          </Stack>
        </LocalizationProvider>
      </DialogContent>

     <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ fontWeight: 800, textTransform: "none" }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <SaveRoundedIcon />}
          sx={{
            fontWeight: 900,
            textTransform: "none",
            borderRadius: 2,
            background: saveForAll
              ? "linear-gradient(90deg,#c62828,#e53935)"
              : "linear-gradient(90deg,#00796b,#26a69a)",
            boxShadow: "0 12px 26px rgba(0,0,0,.16)",
          }}
        >
          {saving ? "Saving..." : saveForAll ? "Save for All" : "Save"}
        </Button>
      </DialogActions>
      </Dialog>
  );
});

export default EditTimeDialog;


